import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import type { InferProps, Validator } from 'prop-types';

import Parallax from './ui/parallax';

import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';

import Action from './action';
import ActionProps from '../prop-types/action';
import Image from 'next/image';

// const toBase64 = str =>
//     typeof window === 'undefined'
//         ? Buffer.from(str).toString('base64')
//         : window.btoa(str);

// const shimmer = (w, h) => `
// <svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
//   <defs>
//     <linearGradient id="g">
//       <stop stop-color="#fff" offset="20%" />
//       <stop stop-color="#f1f1f1" offset="50%" />
//       <stop stop-color="#fff" offset="70%" />
//     </linearGradient>
//   </defs>
//   <rect width="${w}" height="${h}" fill="#fff" />
//   <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
//   <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
// </svg>`;

const animationVariants = {
	visible: { opacity: 1, y: 0 },
	hidden: { opacity: 0, y: 50 },
};

const contentAnimationVariants = {
	visible: { opacity: 1, y: 0 },
	hidden: { opacity: 0, y: 0 },
};

enum ParallaxContentResponsiveLayout {
	/** @default */
	MOBILE_IMAGES = 'mobile-images',
	MOBILE_TEXT = 'mobile-text',
	MOBILE_ALL = 'mobile-all',
}
function isParallaxContentResponsiveLayout(val: unknown): val is ParallaxContentResponsiveLayout {
	const validValues = Object.values(ParallaxContentResponsiveLayout);
	return (validValues as unknown[]).includes(val);
}

const ResponsiveLayoutClass: Record<ParallaxContentResponsiveLayout, string> = {
	[ParallaxContentResponsiveLayout.MOBILE_IMAGES]: '',
	[ParallaxContentResponsiveLayout.MOBILE_TEXT]: 'ParallaxContent--mobile-text',
	[ParallaxContentResponsiveLayout.MOBILE_ALL]: 'ParallaxContent--showMore',
};

const propTypes = {
	block: PropTypes.shape({
		config: PropTypes.shape({
			className: PropTypes.string.isRequired,
			isReverse: PropTypes.bool.isRequired,

			responsiveLayout: function (props, propName, componentName) {
				const value = props[propName] as unknown;

				if (isParallaxContentResponsiveLayout(value)) {
					return null;
				} else if (value === null) {
					return null;
				} else {
					return new Error(`Invalid prop ${propName} supplied to ${componentName}: unrecognised \`responsiveLayout\` value ${value}`);
				}
			} as Validator<ParallaxContentResponsiveLayout | null>,

			/** @deprecated */
			showMore: PropTypes.bool.isRequired,
		}).isRequired,
		content: PropTypes.shape({
			title: PropTypes.string.isRequired,
			parallaxTop: PropTypes.shape({
				src: PropTypes.string.isRequired,
				alt: PropTypes.string.isRequired,
				width: PropTypes.number.isRequired,
				height: PropTypes.number.isRequired,
			}),
			parallaxBottom: PropTypes.shape({
				src: PropTypes.string.isRequired,
				alt: PropTypes.string.isRequired,
				width: PropTypes.number.isRequired,
				height: PropTypes.number.isRequired,
			}),
			image: PropTypes.shape({
				src: PropTypes.string.isRequired,
				alt: PropTypes.string.isRequired,
				width: PropTypes.number.isRequired,
				height: PropTypes.number.isRequired,
				blurDataURL: PropTypes.string.isRequired,
			}).isRequired,
			description: PropTypes.string.isRequired,
			actions: PropTypes.arrayOf(ActionProps),
		}).isRequired,
	}).isRequired,
};
type ParallaxContentProps = InferProps<typeof propTypes>;

const ParallaxContent = Object.assign(
	function ParallaxContent(props: ParallaxContentProps) {
		const {
			block: {
				content,
				config,
			}
		} = props;

		const isQuote = content.title && content.title[0] === '“';

		const [topImgLoaded, setTopImageLoaded] = useState(false);
		const [bottomImgLoaded, setBottomImageLoaded] = useState(false);
		const topAnimationControls = useAnimation();
		const bottomAnimationControls = useAnimation();
		const contentAnimationControls = useAnimation();

		const { ref, inView } = useInView({
			threshold: .3,
		});
		const [topImgRef, topImgInView] = useInView({
			threshold: .8,
		});
		const [bottomImgRef, bottomImgInView] = useInView({
			threshold: .8,
		});
		const [responsiveLayoutClass, setResponsiveLayoutClass] = useState('');

		useEffect(() => {
			if (inView) {
				contentAnimationControls.start('visible');
			}
		}, [inView, contentAnimationControls]);

		useEffect(() => {
			if (topImgInView && topImgLoaded) {
				topAnimationControls.start("visible");
			}
		}, [topImgInView, topImgLoaded, topAnimationControls]);

		useEffect(() => {
			if (bottomImgInView && bottomImgLoaded) {
				bottomAnimationControls.start("visible");
			}
		}, [bottomImgInView, bottomImgLoaded, bottomAnimationControls]);

		useEffect(() => {
			const responsiveLayout = config.responsiveLayout ?? ParallaxContentResponsiveLayout.MOBILE_IMAGES;
			const responsiveLayoutClass = config.showMore ? '' : ResponsiveLayoutClass[responsiveLayout];

			setResponsiveLayoutClass(responsiveLayoutClass);
		}, [config.responsiveLayout, config.showMore]);

		return (
			<div
				ref={ref}
				className={classNames(
					'ParallaxContent container',
					config.className,
					responsiveLayoutClass,
					{ 'ParallaxContent-isReverse': config.isReverse },
					{ 'ParallaxContent--quote': isQuote }
				)}>
				<div className="row ParallaxContent-wrap py-4">
					<div className="col-xs-10 col-xs-offset-1 col-sm-8 col-sm-offset-2 col-md-7 col-md-offset-0 ParallaxContent-image">
						{content?.parallaxTop?.src && (
							<div className="col-sm-5 ParallaxContent-image-top">
								<motion.div
									initial={"hidden"}
									animate={topAnimationControls}
									variants={animationVariants}
									transition={{ ease: [0.16, 1, 0.3, 1], duration: 2, delay: 0 }}
									ref={topImgRef}
								>
									<Parallax offset={20} rerender={topImgLoaded}>
										<Image
											src={content.parallaxTop.src}
											alt={content.parallaxTop.alt}
											height={content.parallaxTop.height || 500}
											width={content.parallaxTop.width || 500}
											layout="responsive"
											onLoadingComplete={() => setTopImageLoaded(true)}
										/>
									</Parallax>
								</motion.div>
							</div>
						)}
						{content?.parallaxBottom?.src && (
							<motion.div
								initial={"hidden"}
								animate={bottomAnimationControls}
								variants={animationVariants}
								transition={{ ease: [0.16, 1, 0.3, 1], duration: 2 }}
								className="col-sm-5 ParallaxContent-image-bottom"
								ref={bottomImgRef}
							>
								<Parallax offset={15} rerender={bottomImgLoaded}>

									<Image
										src={content.parallaxBottom.src}
										alt={content.parallaxBottom.alt}
										height={content.parallaxBottom.height || 500}
										width={content.parallaxBottom.width || 500}
										layout="responsive"
										onLoadingComplete={() => setBottomImageLoaded(true)}
									/>
								</Parallax>
							</motion.div>
						)}
						{content.image && (
							<motion.div
								className="col-sm-12 col-md-10  ParallaxContent-image-bg rellax"
								data-rellax-speed="1"
								initial={"hidden"}
								animate={contentAnimationControls}
								variants={contentAnimationVariants}
								transition={{ ease: [0.37, 0, 0.63, 1], duration: .4 }}
							>
								<Image
									placeholder="blur"
									{...content.image}
									width={content.image.width}
									height={content.image.height}
									blurDataURL={content.image.blurDataURL}
									layout="responsive"
								/>
							</motion.div>
						)}
					</div>
					<div
						// initial={"hidden"}
						// animate={contentAnimationControls}
						// variants={contentAnimationVariants}
						// transition={{ ease: [0.37, 0, 0.63, 1], duration: .4, delay: .2 }}
						className="col-sm-8 col-sm-offset-2 col-md-offset-0 col-md-5 ParallaxContent-details"
					>
						{content.title && (
							<h3 className="ParallaxContent-title h2">
								{content.title}
							</h3>
						)}

						{content.description && (
							<div
								className="ParallaxContent-description"
								dangerouslySetInnerHTML={{
									__html: content.description,
								}}
							/>
						)}

						{content.actions && (
							<div className="ParallaxContent-actions Actions">
								{content.actions.map(function (action, key) {
									return (
										<div key={key}>
											<Action action={action} />
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	},

	{ propTypes }
);

export default ParallaxContent;
